import { SyncService } from "frontend/services/syncService";
import { useEffect, useState } from "react";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { canvasMetadataPrefix } from "shared/util/utils";
import { MappingTaskCardsToMonday, EmptyMappingForTaskCards } from "./use-mapping-settings-task-cards";

export interface MappingMondayToGanttTasks extends MappingTaskCardsToMonday {
  "date-tab": "start-end" | "timeline" | "";
  start_date: string;
  end_date: string;
  timeline: string;
  dependency: string;
}

const DefaultMappingForGantt: MappingMondayToGanttTasks = {
  "date-tab": "timeline",
  start_date: "",
  end_date: "",
  timeline: "",
  dependency: "",
  assignee: "",
  date: "",
  tags: "",
};

export function getMetadataMappingForGantt(columns: {
  [key in keyof typeof DefaultMappingForGantt]?: any;
}): MappingMondayToGanttTasks {
  return {
    ...DefaultMappingForGantt,
    ...columns,
  };
}

export async function commitMappingSettingsForGantt(
  syncService: SyncService<RW> | null,
  boardId: string,
  mapping: MappingMondayToGanttTasks
) {
  await syncService?.mutate.putMetadata({
    id: boardId,
    value: {
      type: "boardColumnsMapping",
      lastModifiedTimestamp: Date.now(),
      boardColumnsMapping: mapping,
    } as any,
  });

  // Save the common fields to task-card mapping object, if it's not present
  const mappingForTaskCards = (await syncService?.mutate.getMetadata(`IntgMapping-${boardId}`)) as any;
  if (!mappingForTaskCards) {
    const taskCardsMapping: MappingTaskCardsToMonday = {
      ...EmptyMappingForTaskCards,
      assignee: mapping.assignee,
      date: mapping.date,
      tags: mapping.tags,
    };

    await syncService?.mutate.putMetadata({
      id: `IntgMapping-${boardId}`,
      value: taskCardsMapping as any,
    });
  }
}

export function useMappingSettingsForGantt(syncService: SyncService<RW> | null, boardId: string | undefined) {
  const [mapping, setMapping] = useState<MappingMondayToGanttTasks | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    if (!boardId) {
      setIsLoading(false);
      setMapping(undefined);
      return;
    }
    syncService
      ?.getReplicache()
      ?.query(async (tx) => {
        const key = `${canvasMetadataPrefix}${boardId}`;
        let mapping: MappingMondayToGanttTasks = ((await tx.get(key)) as any)?.boardColumnsMapping;
        if (!mapping) {
          const fallback: any = await tx.get(`${canvasMetadataPrefix}IntgMapping-${boardId}`);
          if (fallback) {
            mapping = {
              ...DefaultMappingForGantt,
              ...fallback,
            };
          }
        }
        return mapping ?? DefaultMappingForGantt;
      })
      .then(setMapping)
      .finally(() => setIsLoading(false));
  }, [boardId, syncService]);

  return [mapping, setMapping, isLoading] as const;
}
